
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, QrCode } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fhirDataStore } from '@/utils/fhirDataUtils';
import { getUserInfo } from '@/utils/jwtUtils';

interface MedicalItem {
  id: string;
  display: string;
  resourceType: string;
  resource: any;
}

interface QRMedicalDataSelectorProps {
  selectedPatientId: string;
  onProfileCreated: (profileId: string, qrUrl: string) => void;
}

export const QRMedicalDataSelector = ({ selectedPatientId, onProfileCreated }: QRMedicalDataSelectorProps) => {
  const [medications, setMedications] = useState<MedicalItem[]>([]);
  const [conditions, setConditions] = useState<MedicalItem[]>([]);
  const [allergies, setAllergies] = useState<MedicalItem[]>([]);
  const [selectedMedications, setSelectedMedications] = useState<string[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedAllergies, setSelectedAllergies] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [patientDemographics, setPatientDemographics] = useState<any>(null);
  const [existingProfileId, setExistingProfileId] = useState<string | null>(null);

  const { toast } = useToast();

  useEffect(() => {
    loadMedicalData();
    loadExistingProfile();
  }, [selectedPatientId]);

  const loadExistingProfile = () => {
    // Check if there's an existing profile for this patient
    const storageKey = selectedPatientId === 'me' ? 'public_profile_current_user' : `public_profile_${selectedPatientId}`;
    const existingProfile = localStorage.getItem(storageKey);
    if (existingProfile) {
      try {
        const profileData = JSON.parse(existingProfile);
        setExistingProfileId(profileData.profileId);
      } catch (error) {
        console.error('Error parsing existing profile:', error);
      }
    }
  };

  const loadMedicalData = async () => {
    setIsLoading(true);
    try {
      let bundle;
      
      if (selectedPatientId === 'me') {
        // Load from localStorage for current user
        const storedData = localStorage.getItem('fhir_bundle');
        if (storedData) {
          bundle = JSON.parse(storedData);
        }
      } else {
        // Load from IndexedDB for other patients
        bundle = await fhirDataStore.getFHIRData(selectedPatientId);
      }

      if (!bundle || !bundle.entry) {
        console.error('No FHIR bundle found');
        return;
      }

      // Extract patient demographics
      const patientEntry = bundle.entry.find((entry: any) => 
        entry.resource?.resourceType === 'Patient'
      );
      
      if (patientEntry) {
        setPatientDemographics(patientEntry.resource);
      }

      // Extract medications
      const medicationEntries = bundle.entry.filter((entry: any) => 
        entry.resource?.resourceType === 'MedicationRequest' || 
        entry.resource?.resourceType === 'MedicationStatement'
      );
      
      const medItems = medicationEntries.map((entry: any, index: number) => ({
        id: entry.resource.id || `med-${index}`,
        display: entry.resource.medicationCodeableConcept?.text || 
                 entry.resource.medicationCodeableConcept?.coding?.[0]?.display || 
                 'Unknown Medication',
        resourceType: entry.resource.resourceType,
        resource: entry.resource
      }));

      // Extract conditions
      const conditionEntries = bundle.entry.filter((entry: any) => 
        entry.resource?.resourceType === 'Condition'
      );
      
      const condItems = conditionEntries.map((entry: any, index: number) => ({
        id: entry.resource.id || `cond-${index}`,
        display: entry.resource.code?.text || 
                 entry.resource.code?.coding?.[0]?.display || 
                 'Unknown Condition',
        resourceType: entry.resource.resourceType,
        resource: entry.resource
      }));

      // Extract allergies
      const allergyEntries = bundle.entry.filter((entry: any) => 
        entry.resource?.resourceType === 'AllergyIntolerance'
      );
      
      const allergyItems = allergyEntries.map((entry: any, index: number) => ({
        id: entry.resource.id || `allergy-${index}`,
        display: entry.resource.code?.text || 
                 entry.resource.code?.coding?.[0]?.display || 
                 'Unknown Allergy',
        resourceType: entry.resource.resourceType,
        resource: entry.resource
      }));

      setMedications(medItems);
      setConditions(condItems);
      setAllergies(allergyItems);

    } catch (error) {
      console.error('Error loading medical data:', error);
      toast({
        title: "Error",
        description: "Failed to load medical data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMedicationToggle = (medicationId: string) => {
    setSelectedMedications(prev => 
      prev.includes(medicationId) 
        ? prev.filter(id => id !== medicationId)
        : [...prev, medicationId]
    );
  };

  const handleConditionToggle = (conditionId: string) => {
    setSelectedConditions(prev => 
      prev.includes(conditionId) 
        ? prev.filter(id => id !== conditionId)
        : [...prev, conditionId]
    );
  };

  const handleAllergyToggle = (allergyId: string) => {
    setSelectedAllergies(prev => 
      prev.includes(allergyId) 
        ? prev.filter(id => id !== allergyId)
        : [...prev, allergyId]
    );
  };

  const handleGenerateProfile = async () => {
    const userInfo = getUserInfo();
    if (!userInfo) {
      toast({
        title: "Error",
        description: "Authentication required",
        variant: "destructive"
      });
      return;
    }

    if (!patientDemographics) {
      toast({
        title: "Error", 
        description: "Patient demographics not found",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Transform medical data to match MedicalInfo interface structure
      const transformedMedicalData = {
        medications: medications
          .filter(med => selectedMedications.includes(med.id))
          .map(med => ({
            name: med.display,
            dosage: med.resource.dosageInstruction?.[0]?.text || 
                   med.resource.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity?.value + ' ' +
                   med.resource.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity?.unit || 
                   undefined
          })),
        conditions: conditions
          .filter(cond => selectedConditions.includes(cond.id))
          .map(cond => ({
            name: cond.display,
            status: cond.resource.clinicalStatus?.coding?.[0]?.code || 
                   cond.resource.verificationStatus?.coding?.[0]?.code || 
                   undefined
          })),
        allergies: allergies
          .filter(allergy => selectedAllergies.includes(allergy.id))
          .map(allergy => ({
            substance: allergy.display,
            severity: allergy.resource.criticality || 
                     allergy.resource.reaction?.[0]?.severity || 
                     undefined
          })),
        emergencyContact: {
          name: "Emergency Contact", // This would need to come from patient data or be collected separately
          phone: "911" // Default emergency number
        }
      };

      // Extract patient name and DOB
      const patientName = patientDemographics.name?.[0] ? 
        `${patientDemographics.name[0].given?.join(' ') || ''} ${patientDemographics.name[0].family || ''}`.trim() :
        'Unknown Patient';
      
      const dateOfBirth = patientDemographics.birthDate;

      const profileData = {
        patientName,
        dateOfBirth,
        medicalData: transformedMedicalData,
        existingProfileId // Include existing profile ID for updates
      };

      const token = localStorage.getItem('bearer_token');
      const response = await fetch('/api/public_profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create public profile');
      }

      const result = await response.json();

      // Save profile info to localStorage
      const storageKey = selectedPatientId === 'me' ? 'public_profile_current_user' : `public_profile_${selectedPatientId}`;
      const profileInfo = {
        profileId: result.profileId,
        publicUrl: result.publicUrl,
        patientName,
        dateOfBirth,
        createdAt: new Date().toISOString()
      };
      localStorage.setItem(storageKey, JSON.stringify(profileInfo));

      toast({
        title: "Success",
        description: existingProfileId ? "Public profile updated successfully" : "Public profile created successfully",
      });

      onProfileCreated(result.profileId, result.publicUrl);

    } catch (error) {
      console.error('Error creating profile:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create public profile",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Loading medical data...</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Select Medical Information for QR Code</h3>
        <p className="text-muted-foreground mb-6">
          Choose which medical information to include in your public profile QR code. This will be accessible to emergency responders and healthcare providers.
        </p>

        {/* Medications Section */}
        {medications.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium mb-3">Current Medications ({medications.length})</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {medications.map((medication) => (
                <div key={medication.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`med-${medication.id}`}
                    checked={selectedMedications.includes(medication.id)}
                    onCheckedChange={() => handleMedicationToggle(medication.id)}
                  />
                  <label 
                    htmlFor={`med-${medication.id}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {medication.display}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conditions Section */}
        {conditions.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium mb-3">Medical Conditions ({conditions.length})</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {conditions.map((condition) => (
                <div key={condition.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`cond-${condition.id}`}
                    checked={selectedConditions.includes(condition.id)}
                    onCheckedChange={() => handleConditionToggle(condition.id)}
                  />
                  <label 
                    htmlFor={`cond-${condition.id}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {condition.display}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Allergies Section */}
        {allergies.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium mb-3">Allergies & Intolerances ({allergies.length})</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {allergies.map((allergy) => (
                <div key={allergy.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`allergy-${allergy.id}`}
                    checked={selectedAllergies.includes(allergy.id)}
                    onCheckedChange={() => handleAllergyToggle(allergy.id)}
                  />
                  <label 
                    htmlFor={`allergy-${allergy.id}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {allergy.display}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {medications.length === 0 && conditions.length === 0 && allergies.length === 0 && (
          <p className="text-muted-foreground text-center py-4">
            No medical data found. Please import your medical records first.
          </p>
        )}

        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleGenerateProfile}
            disabled={isSubmitting || (selectedMedications.length === 0 && selectedConditions.length === 0 && selectedAllergies.length === 0)}
            className="flex items-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Creating Profile...</span>
              </>
            ) : (
              <>
                <QrCode className="h-4 w-4" />
                <span>Generate QR Code</span>
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};
